import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export const CALENDLY_URL = "https://calendly.com/stuart-corpfive/30min";

const STAGES = [
  "Just exploring — is this real?",
  "Setting up my wallet",
  "Funded — what do I do next?",
  "Earning / staking questions",
  "Cashing out to my bank",
  "Building my team / downline",
  "Something went wrong / I'm stuck",
];
const TIMELINES = ["This week", "This month", "Just researching for now"];

// Build a Calendly link with the prospect's context pre-filled so Stuart
// walks into the call already knowing the issue. name/email use Calendly's
// standard prefill; a1 maps to Calendly's "anything to help prepare?" question.
export function buildCalendlyUrl({ name, email, stage, timeline, issue }) {
  const summary = [
    stage ? `Stage: ${stage}` : null,
    timeline ? `Timeline: ${timeline}` : null,
    issue ? `What they need: ${issue}` : null,
  ].filter(Boolean).join("  |  ");
  const params = new URLSearchParams();
  if (name) params.set("name", name);
  if (email) params.set("email", email);
  if (summary) params.set("a1", summary);
  const qs = params.toString();
  return qs ? `${CALENDLY_URL}?${qs}` : CALENDLY_URL;
}

export function BookCallModal({ open, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", stage: "", timeline: "", issue: "" });

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, name: f.name || user?.name || "", email: f.email || user?.email || "" }));
  }, [open, user]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    window.open(buildCalendlyUrl(form), "_blank", "noopener,noreferrer");
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="bookcall-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.42)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="relative glass w-full max-w-md p-6 max-h-[92vh] overflow-y-auto" style={{ borderRadius: 20 }}>
        <button onClick={onClose} className="absolute top-4 right-4 org-icon-btn" title="Close" data-testid="bookcall-close"><X size={16} /></button>
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg, rgba(64,96,255,0.16), rgba(107,79,255,0.16))" }}>
          <CalendarDays size={20} style={{ color: "#4060FF" }} />
        </div>
        <div className="kpi-label">Book a 30-min call with Stuart</div>
        <h3 className="font-display text-xl font-semibold tracking-tight mb-1">A couple quick questions first</h3>
        <p className="text-sm text-ink-2 mb-5">So Stuart can prep and make every minute count for you.</p>

        <form onSubmit={submit} className="space-y-3.5" data-testid="bookcall-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-ink-3 font-medium block mb-1.5">Your name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="input-glass w-full" data-testid="bookcall-name" />
            </div>
            <div>
              <label className="text-xs text-ink-3 font-medium block mb-1.5">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="input-glass w-full" data-testid="bookcall-email" />
            </div>
          </div>

          <div>
            <label className="text-xs text-ink-3 font-medium block mb-1.5">Where are you right now? *</label>
            <select required value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="input-glass w-full" data-testid="bookcall-stage">
              <option value="">Choose your stage…</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-ink-3 font-medium block mb-1.5">What's the main thing you want help with? *</label>
            <textarea required rows={3} value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} placeholder="e.g. My USDT isn't showing in TP Wallet / I want to understand staking returns / Help me bring my first 3 people on…" className="input-glass w-full resize-none" data-testid="bookcall-issue" />
          </div>

          <div>
            <label className="text-xs text-ink-3 font-medium block mb-1.5">How soon do you want to move?</label>
            <select value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} className="input-glass w-full" data-testid="bookcall-timeline">
              <option value="">No rush / not sure</option>
              {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <button type="submit" className="btn-primary w-full justify-center mt-1" data-testid="bookcall-submit">
            Continue to booking <ArrowRight size={15} />
          </button>
          <p className="text-[11px] text-ink-3 text-center">Your answers travel with the invite so Stuart is ready before you connect.</p>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function BookCall({ className = "btn-primary", label = "Book a call with Stuart", style }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} style={style} data-testid="book-call-btn">
        <CalendarDays size={15} /> {label}
      </button>
      <BookCallModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
